import { t } from "i18next";
import type { SetStateAction } from "react";
import credLogo from "assests/images/credLogo.svg";
import tickerTapeLogo from "assests/images/tickerTapeLogo.svg";
import { ErrorType } from "lib/types/address";
import { SavingItemsType } from "lib/types/checkout";

export type CouponCategory =
  | "ALL"
  | "BRAND_OFFERS"
  | "PAYMENT_OFFERS"
  | "SHIPPING_OFFERS"
  | "PG_OFFERS"
  | "PARTNER_OFFERS"
  | "UNAVAILABLE";

export interface CouponDialogProps {
  setOpenDialog: () => void;
  handleDiscountApply: (
    code: string,
    isManual: boolean,
    overrideRemoval?: boolean,
    couponTitle?: string,
  ) => Promise<boolean>;
  handleDeleteCoupon: (couponToBeDeleted: string) => void;
  savingItems: SavingItemsType;
  couponDisplayType?: string;
  isCouponsLoading: boolean;
  couponApplyError: ErrorType;
  setCouponApplyError: (value: SetStateAction<ErrorType>) => void;
  initialFilter?: string;
  onCollectPartnerOffer?: (offerIds: string[]) => Promise<void>;
  onRemovePartnerOffer?: (offerIds: string[]) => Promise<void>;
}

type WalletType = "CRED" | "TICKERTAPE";

interface CouponGroup {
  category: CouponCategory;
  coupons: any[];
}

const rewardLogoMap: Record<WalletType, string> = {
  CRED: credLogo,
  TICKERTAPE: tickerTapeLogo,
};

const rewardTitleMap: Record<WalletType, string> = {
  CRED: "cred_reward_header",
  TICKERTAPE: "ticker_tape_reward_header",
};

const rewardHeaderMap: Record<WalletType, string> = {
  CRED: "cashback_amount",
  TICKERTAPE: "ticker_tape_gold_amount",
};

const rewardDescriptionMap: Record<WalletType, string> = {
  CRED: "claim_cred_cashback",
  TICKERTAPE: "claim_ticker_tape_cashback",
};

const bankOfferLogoMap: Record<string, string> = {
  HDFC: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/hdfc.png",
  ICICI: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/icici.png",
  SBI: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/sbi.png",
  AXIS: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/axis.png",
  KOTAK: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/kotak.png",
  YES_BANK: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/yes.png",
  INDUSIND: "cashfreelogo.cashfree.com/assets_images/pg/nb/64/indusind.png",
  STANDARD_CHARTERED: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/scb.png",
  CITI: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/citi.png",
  HSBC: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/hsb.png",
  IDFC: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/idfc.png",
  RBL: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/rbl.png",
  FEDERAL: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/federal.png",
  IDBI: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/idbi.png",
  PUNJAB_NATIONAL: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/pnbc.png",
  BANK_OF_BARODA: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/bobc.png",
  CANARA: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/canara.png",
  UNION_BANK: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/union.png",
  INDIAN_BANK: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/indian.png",
  BOI: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/boi.png",
  CENTRAL_BANK: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/cbi.png",
  INDIAN_OVERSEAS: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/iob.png",
  UCO: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/uco.png",
  BANK_OF_MAHARASHTRA: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/bom.png",
  PUNJAB_AND_SIND: "https://cashfreelogo.cashfree.com/assets_images/pg/nb/64/pnsb.png",
  PAYTM: "https://cashfreelogo.cashfree.com/assets_images/pg/wallet/64/paytm.png",
  AIRTEL: "https://cashfreelogo.cashfree.com/assets_images/pg/wallet/64/airtel.png",
  FINO: "",
  ALL: "",
  UNKNOWN: "",
};

export const mapRewardLogo = (walletType: WalletType) => rewardLogoMap[walletType] || credLogo;

export const mapRewardTitle = (walletType: WalletType) => t(rewardTitleMap[walletType]);

export const mapRewardHeader = (walletType: WalletType, amount: number | string) =>
  t(rewardHeaderMap[walletType], { amount });

export const mapRewardDescription = (walletType: WalletType) => t(rewardDescriptionMap[walletType]);

export const isValidPartnerOffer = (offer: any): boolean =>
  Boolean(offer?.offer_id) && !offer?.coupon_details;

const isUnavailableCoupon = (coupon: any): boolean => {
  const type = coupon?.coupon_details?.coupon_type;
  const reason = coupon?.un_applicability_reason?.reason;

  // UARC_002, UARC_002 => Can be applied but not combinable

  return type !== "CALLOUT_CARD" && !coupon?.applicable && reason !== "UARC_002" && reason !== "UARC_007";
};

const getCouponCategory = (coupon: any): CouponCategory => {
  const type = coupon?.coupon_details?.coupon_type;

  if (isUnavailableCoupon(coupon)) return "UNAVAILABLE";
  if (type === "PAYMENT_OFFER") return "PAYMENT_OFFERS";
  if (type === "SHIPPING") return "SHIPPING_OFFERS";
  return "BRAND_OFFERS";
};

export const getAppliedCouponCategory = (coupon: { couponType?: string }): CouponCategory => {
  if (coupon?.couponType === "PAYMENT_OFFER") return "PAYMENT_OFFERS";
  if (coupon?.couponType === "SHIPPING") return "SHIPPING_OFFERS";
  return "BRAND_OFFERS";
};

export const categorizeCoupons = (coupons: any[] = [], bankOffers: any[] = [], partnerOffers: any[] = []): CouponGroup[] => {
  const groups: Record<CouponCategory, any[]> = {
    ALL: [],
    BRAND_OFFERS: [],
    PAYMENT_OFFERS: [],
    SHIPPING_OFFERS: [],
    PG_OFFERS: [],
    PARTNER_OFFERS: [],
    UNAVAILABLE: [],
  };

  coupons.forEach((coupon) => {
    const category = getCouponCategory(coupon);
    groups.ALL.push(coupon);
    groups[category].push(coupon);
  });

  const unavailable = groups.UNAVAILABLE;

  const groupedCoupons = Object.entries(groups)
    .filter(([category, coupons]) => coupons.length > 0 && category !== "UNAVAILABLE")
    .map(([category, coupons]) => ({
      category: category as CouponCategory,
      coupons,
    }));

  if (bankOffers.length > 0) {
    groupedCoupons.push({
      category: "PG_OFFERS",
      coupons: bankOffers,
    });
  }

  if (partnerOffers.length > 0) {
    groupedCoupons.push({
      category: "PARTNER_OFFERS",
      coupons: partnerOffers,
    });
  }

  if (unavailable.length > 0) {
    groupedCoupons.push({
      category: "UNAVAILABLE",
      coupons: unavailable,
    });
  }

  return groupedCoupons;
};

export const getBankIcon = (bankName: string) =>
  Boolean(bankOfferLogoMap[bankName]) ? bankOfferLogoMap[bankName] : "";

export function extractRupeeValue(text: string): number {
  const match = text.match(/₹\s*(\d+(?:,\d+)*(?:\.\d+)?)/);
  if (!match) return 0;
  return parseFloat(match[1].replace(/,/g, ""));
}
